import { Option, Select, OnChangeParams } from "baseui/select";
import { useState, useEffect } from "react";
import { useBusinessAccessTypes, useInvitationTypes } from "../../../hooks/useBusinessAccessType";
import { useT } from "../../../i18n/admin";

/** The tenant's Guest pass type, matched like the backend's front_desk_pass_type (case and spaces ignored). */
export const isGuestTypeName = (name: unknown): boolean => typeof name === 'string' && name.trim().toLowerCase() === 'guest';

/** The tenant's Client Booking pass type, matched by name like the backend (SAM-814), not by id. */
export const isClientBookingTypeName = (name: unknown): boolean =>
  typeof name === 'string' && name.trim().toLowerCase() === 'client booking';

/**
 * The pass types a business role (business_admin, business_invitation) gives in its own business: Guest and Client
 * Booking (SAM-1327, B17). Guest keeps its id 1 fallback for a tenant whose type is named otherwise.
 */
export const ownBusinessPassTypes = <T extends { id?: unknown; label?: unknown }>(options: T[]): T[] =>
  options.filter((o) => isGuestTypeName(o.label) || o.id === 1 || isClientBookingTypeName(o.label));

/**
 * What a business role's control shows when what it shows is not one of its types (nothing yet, or a type of another
 * kind): the tenant's Guest. `null` when there is nothing to change.
 */
export const ownBusinessTypeFallback = <T extends { id?: unknown; label?: unknown }>(opts: {
  shown?: { id?: unknown } | null;
  options: T[];
}): T | null => {
  if (opts.options.length === 0) return null;
  if (opts.shown && opts.options.some((o) => o.id === opts.shown!.id)) return null;
  return opts.options.find((o) => isGuestTypeName(o.label)) ?? opts.options.find((o) => o.id === 1) ?? opts.options[0];
};

export type SelectOptions = Option;

/**
 * What the control must do with the type it shows (SAM-1320, B2). The form state can be emptied behind the control
 * (switching the pass kind resets `accessType`), and the control kept showing the previous kind's «Guest» while
 * the form sent no type at all (`business_access_type_id is required`).
 * - `default`: nothing shown, or what is shown is not an option of this kind: pick the default;
 * - `emit`: what is shown is valid but the form has none: hand it to the form;
 * - `keep`: nothing to do.
 */
export const accessTypeSyncAction = (opts: {
  shown?: { id?: unknown; label?: unknown } | null;
  options: Array<{ id?: unknown; label?: unknown }>;
  formHasValue: boolean;
}): 'default' | 'emit' | 'keep' => {
  if (opts.options.length === 0) return 'keep';
  const shown = opts.shown;
  const valid = !!shown && opts.options.some((o) => o.id === shown.id && o.label === shown.label);
  if (!valid) return 'default';
  return opts.formHasValue ? 'keep' : 'emit';
};
type Prop = {
  defaultValue?: SelectOptions[];
  onChange?: (value: SelectOptions[]) => void;
  disabled?: boolean;
  ownBusinessOnly?: boolean;
  kind?: 'business' | 'residential';
};

export const AccessTypeControl = ({ defaultValue, onChange, disabled = false, ownBusinessOnly = false, kind = 'residential' }: Prop) => {
  // Fetch access types based on kind
  const { data: businessAccessTypes, isLoading: isLoadingBusiness, error: businessError } = useBusinessAccessTypes();
  const { data: invitationTypes, isLoading: isLoadingInvitation, error: invitationError } = useInvitationTypes();

  const [value, setValue] = useState<SelectOptions[]>([]);
  const { t } = useT();

  // Determine which data to use based on kind
  const accessTypesData = kind === 'business' ? businessAccessTypes : invitationTypes;
  const isLoading = kind === 'business' ? isLoadingBusiness : isLoadingInvitation;
  const error = kind === 'business' ? businessError : invitationError;

  // Debug logging
  useEffect(() => {
  }, [kind, businessAccessTypes, invitationTypes, accessTypesData, isLoading, error]);

  // Map access types to options - accessTypesData should be an array.
  // Carry the per-type guest limits (max_adults / max_children /
  // max_guests_per_day) on the option so the guest-count step can validate
  // against them (SAM-154). They are undefined when the backend leaves them
  // blank ("unlimited").
  // SAM-256: an invitation type can eventually be scoped to Residential
  // and/or Business, but the backend has no field for it yet (tracked as
  // SAM-269) and the scope is not persisted anywhere. Until then, both
  // lists are shown unfiltered — the residential list (invitation_type)
  // and the business list (business_access_type) remain the two separate
  // tables this ticket does not touch.
  const accessTypeOptions = (accessTypesData && Array.isArray(accessTypesData))
    ? accessTypesData
        .map((type: any) => ({
          label: type.name,
          id: type.id,
          max_adults: type.max_adults,
          max_children: type.max_children,
          max_guests_per_day: type.max_guests_per_day,
        }))
    : [];

  // Filter options based on permissions and sort with Guest first
  const filteredOptions = (() => {
    const allowed = ownBusinessOnly ? ownBusinessPassTypes(accessTypeOptions) : accessTypeOptions;
    // Separate Guest option from others
    const guestOption = allowed.find((option: any) => option.id === 1);
    const otherOptions = allowed.filter((option: any) => option.id !== 1);

    // Sort other options alphabetically
    const sortedOtherOptions = otherOptions.sort((a: any, b: any) =>
      a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' })
    );

    // Return Guest first, then sorted others
    return guestOption ? [guestOption, ...sortedOtherOptions] : sortedOtherOptions;
  })();

  const handleChange = (params: OnChangeParams) => {
    setValue(params.value as SelectOptions[]);
    onChange?.(params.value as SelectOptions[]);
  };

  // A business role starts on its Guest, and never shows a type that is not one of its own
  useEffect(() => {
    if (!ownBusinessOnly) return;
    const fallback = ownBusinessTypeFallback({ shown: value[0], options: filteredOptions });
    if (fallback) {
      setValue([fallback]);
      onChange?.([fallback]);
    }
  }, [ownBusinessOnly, accessTypeOptions, onChange]);

  // Set default value when data is loaded or when defaultValue prop changes
  useEffect(() => {
    if (defaultValue && defaultValue.length > 0) {
      setValue(defaultValue);
      return;
    }
    if (ownBusinessOnly) return;
    const action = accessTypeSyncAction({ shown: value[0], options: accessTypeOptions, formHasValue: false });
    if (action === 'emit') {
      onChange?.(value);
    } else if (action === 'default') {
      // Default to "Guest" if available, otherwise first option. business_access_type is per tenant (ids
      // differ), so Guest is found by name the way the backend does (S-47, lower(name) = 'guest'); id 1 is
      // only the fallback for a tenant whose type is named otherwise.
      const guestOption = accessTypeOptions.find((option: any) => isGuestTypeName(option.label))
        ?? accessTypeOptions.find((option: any) => option.id === 1);
      const defaultOption = guestOption || accessTypeOptions[0];
      setValue([defaultOption]);
      onChange?.([defaultOption]);
    }
  }, [defaultValue, accessTypeOptions, ownBusinessOnly]);

  return (
    <Select
      value={value}
      options={filteredOptions}
      onChange={handleChange}
      disabled={disabled || isLoading || !!error}
      placeholder={
        isLoading 
          ? t(kind === 'business' ? "booking.accessType.loadingBusiness" : "booking.accessType.loadingResidential")
          : error 
          ? t(kind === 'business' ? "booking.accessType.errorBusiness" : "booking.accessType.errorResidential")
          : t("booking.accessType.select")
      }
      clearable={false}
      searchable={false}
      overrides={{
        Popover: {
          props: {
            overrides: {
              Body: {
                style: {
                  zIndex: '999999 !important',
                },
              },
            },
          },
        },
        Dropdown: {
          style: {
            zIndex: '999999 !important',
          },
        },
      }}
    />
  );
};
